import React from 'react';
import { Highlight, themes } from 'prism-react-renderer';

/**
 * BlogCodeBlock - Code block with syntax highlighting
 * Uses prism-react-renderer with Dracula theme
 * 
 * @param {Object} props
 * @param {string} props.code - Code content
 * @param {string} [props.language='javascript'] - Programming language
 * @param {string} [props.filename] - Optional filename to display
 * @param {boolean} [props.showLineNumbers=false] - Show line numbers
 */
export const BlogCodeBlock = ({ 
  code, 
  language = 'javascript',
  filename,
  showLineNumbers = false
}) => {
  // Clean up code - remove trailing newlines
  const cleanCode = code.trim();

  return (
    <div 
      className="mt-6 mb-6 rounded-lg overflow-hidden"
      data-testid="blog-code-block"
    >
      {filename && (
        <div className="bg-[#282A36] px-4 py-2 border-b border-gray-700">
          <span className="text-gray-400 text-sm font-mono">{filename}</span>
        </div>
      )}
      <Highlight
        theme={themes.dracula}
        code={cleanCode}
        language={language}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre 
            className={`${className} p-6 overflow-x-auto`}
            style={{ ...style, margin: 0 }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {showLineNumbers && (
                  <span className="inline-block w-8 text-gray-500 text-right mr-4 select-none">
                    {i + 1}
                  </span>
                )}
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

/**
 * BlogInlineCode - Inline code styling
 */
export const BlogInlineCode = ({ children }) => {
  return (
    <code 
      className="font-mono text-sm bg-[#1E1E1E] text-gray-200 px-1.5 py-0.5 rounded"
      data-testid="blog-inline-code"
    >
      {children}
    </code>
  );
};

export default BlogCodeBlock;
